import { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

const CURRENT_POLICY_VERSION = 'v1.0-2025-09';

export function useConsentimientoCliente(
  idEmpresa: string | undefined,
  email: string | null
) {
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ============================================================
  // Obtener estado de consentimiento
  // ============================================================
  const fetchConsent = useCallback(async () => {
    if (!idEmpresa || !email) {
      setAccepted(null);
      return;
    }

    if (!email.includes('.')) return;

    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      'verificar_consentimiento_por_email',
      {
        p_id_empresa: idEmpresa,
        p_email: email,
        p_policy_version: CURRENT_POLICY_VERSION,
      }
    );

    if (rpcError) {
      Sentry.captureException(rpcError, { extra: { idEmpresa, email } }); // 👈
      setError('Error verificando consentimiento');
      setAccepted(false);
      setLoading(false);
      return;
    }

    setAccepted(data === true);
    setLoading(false);
  }, [idEmpresa, email]);

  useEffect(() => {
    fetchConsent();
  }, [fetchConsent]);

  // ============================================================
  // Guardar consentimiento
  // ============================================================
  const saveConsent = useCallback(
    async (telefono: string | null) => {
      if (!idEmpresa || !email)
        return { ok: false, error: 'Datos incompletos' };

      const { error } = await supabase.from('usuario_consentimientos').insert([
        {
          id_empresa: idEmpresa,
          email,
          telefono,
          acepta_tratamiento: true,
          acepta_comunicaciones: false,
          policy_version: CURRENT_POLICY_VERSION,
          policy_text:
            'Acepto el tratamiento de mis datos personales según la Política de Privacidad vigente.',
        },
      ]);

      if (error) {
        Sentry.captureException(error, { extra: { idEmpresa, email } }); // 👈
        return { ok: false, error: 'No se pudo guardar el consentimiento' };
      }

      setAccepted(true);
      return { ok: true };
    },
    [idEmpresa, email]
  );

  return {
    accepted,
    loading,
    error,
    fetchConsent,
    saveConsent,
  };
}
